/**
 * PublicHeader Component
 * 
 * Two-tier header for public/unauthenticated pages with WHITE background.
 * 
 * Top Bar (White):
 *   - BLS logo (dark blue) + "Apply for VISA to Spain In India" tagline (gold)
 *   - "Follow us on" with social media icons
 *   - Language selector + Login + Agent Login buttons
 */

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { AtSign, FileSearch, Lock } from 'lucide-react';
import { cn } from '@/shared/utils/commonUtils';
import { Button } from '@/shared/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { LanguageSwitcher } from '@/shared/components/LanguageSwitcher';
import { PublicTrackStatusSheet } from '@/shared/components/PublicTrackStatusSheet';
import { useBoundStore } from '@/stores';
import { environment } from '@/config/environment';

// Social media icons with brand colors
const SocialIcon = ({
  href,
  bgColor,
  children,
  className,
}: {
  href: string;
  bgColor: string;
  children: React.ReactNode;
  className?: string;
}) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className={cn(
      "w-8 h-8 rounded-full flex items-center justify-center text-white transition-opacity hover:opacity-80",
      bgColor,
      className
    )}
  >
    {children}
  </a>
);

// Facebook Icon
const FacebookIcon = () => (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

// Instagram Icon
const InstagramIcon = () => (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
  </svg>
);

// X (Twitter) Icon
const XIcon = () => (
  <span className="text-sm font-black leading-none">𝕏</span>
);

// LinkedIn Icon
const LinkedInIcon = () => (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

export const PublicHeader = () => {
  const { t } = useTranslation('common');
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const countryName = useBoundStore.use.countryName();
  const clientName = useBoundStore.use.clientName();

  const trackSheetOpen = searchParams.get('track') === 'open';
  const setTrackSheetOpen = useCallback(
    (open: boolean) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (open) {
          next.set('track', 'open');
        } else {
          next.delete('track');
        }
        return next;
      }, { replace: true });
    },
    [setSearchParams],
  );

  const hasClient = clientName != null && clientName.trim() !== '';
  const hasCountry = countryName != null && countryName.trim() !== '';
  const tagline =
    hasClient && hasCountry
      ? t('layout.header.taglineBoth', { clientName, countryName })
      : hasClient
        ? t('layout.header.taglineClient', { clientName })
        : hasCountry
          ? t('layout.header.taglineCountry', { countryName })
          : t('layout.header.taglineFallback');

  return (
    <header className="w-full shrink-0 sticky top-0 z-50 transition-all duration-300">
      {/* Top inset from env only — avoid min-height padding on small widths (e.g. max + 2.75rem) which looked like extra gap below the status bar; shrink-0 on <header> still prevents flex squash */}
      <div className="bg-white/80 backdrop-blur-md border-b border-gray-200 shadow-sm supports-[backdrop-filter]:bg-white/60 pt-[env(safe-area-inset-top,0px)]">
        <div className="mx-auto px-2 sm:px-4 lg:px-6">
          <div className="flex items-center justify-between gap-3 h-16 lg:h-20 transition-all duration-300 ease-in-out">
            {/* Logo & Tagline */}
            <div className="flex items-center gap-3 sm:gap-5 lg:gap-6 group min-w-0 flex-1">
              <Link to="/" className="flex-shrink-0 transition-transform duration-300 group-hover:scale-105 touch-manipulation">
                <img
                  src="/bls-logo.png"
                  alt="BLS International"
                  className="h-9 sm:h-10 lg:h-12 w-auto"
                />
              </Link>
              <span
                className="hidden lg:block text-[#c0902c] text-2xl font-bold tracking-tight drop-shadow-sm truncate min-w-0"
                title={tagline}
              >
                {tagline}
              </span>
            </div>

            {/* Desktop: Social, Language, Login/Sign up, Track */}
            <div className="hidden lg:flex items-center gap-5 shrink-0">
              <div className="flex items-center gap-3">
                <span className="text-gray-500 text-xs font-medium tracking-wide">{t('layout.header.followUsOn')}</span>
                <div className="flex items-center gap-2">
                  {environment.SOCIAL_FACEBOOK_URL && (
                    <SocialIcon href={environment.SOCIAL_FACEBOOK_URL} bgColor="bg-[#1877F2] hover:scale-110 hover:shadow-lg hover:shadow-blue-500/20">
                      <FacebookIcon />
                    </SocialIcon>
                  )}
                  {environment.SOCIAL_INSTAGRAM_URL && (
                    <SocialIcon href={environment.SOCIAL_INSTAGRAM_URL} bgColor="bg-gradient-to-br from-[#f09433] via-[#dc2743] to-[#bc1888] hover:scale-110 hover:shadow-lg hover:shadow-pink-500/20">
                      <InstagramIcon />
                    </SocialIcon>
                  )}
                  {environment.SOCIAL_TWITTER_URL && (
                    <SocialIcon href={environment.SOCIAL_TWITTER_URL} bgColor="bg-black hover:scale-110 hover:shadow-lg hover:shadow-gray-500/20">
                      <XIcon />
                    </SocialIcon>
                  )}
                  {environment.SOCIAL_LINKEDIN_URL && (
                    <SocialIcon href={environment.SOCIAL_LINKEDIN_URL} bgColor="bg-[#0A66C2] hover:scale-110 hover:shadow-lg hover:shadow-blue-600/20">
                      <LinkedInIcon />
                    </SocialIcon>
                  )}
                </div>
              </div>
              <LanguageSwitcher className="hidden lg:flex" />
              {(location.pathname === '/' || location.pathname === '/login') ? (
                <Link
                  to="/signup"
                  className="group relative flex items-center gap-2 bg-[#c0902c] hover:bg-[#b08020] text-white px-6 py-2.5 rounded-full text-sm font-semibold shadow-md shadow-orange-900/10 hover:shadow-lg transition-all duration-300"
                >
                  <Lock className="w-4 h-4 transition-transform group-hover:scale-110" />
                  <span>{t('navigation.signup')}</span>
                </Link>
              ) : (
                <Link
                  to="/login"
                  className="group relative flex items-center gap-2 bg-[#c0902c] hover:bg-[#b08020] text-white px-6 py-2.5 rounded-full text-sm font-semibold shadow-md shadow-orange-900/10 hover:shadow-lg transition-all duration-300"
                >
                  <Lock className="w-4 h-4 transition-transform group-hover:scale-110" />
                  <span>{t('navigation.login')}</span>
                </Link>
              )}
              <button
                type="button"
                onClick={() => setTrackSheetOpen(true)}
                className="group relative flex items-center gap-2 bg-[#2e5784] hover:bg-[#234568] text-white px-5 py-2.5 rounded-full text-sm font-semibold shadow-md shadow-blue-900/10 hover:shadow-lg transition-all duration-300"
              >
                <FileSearch className="w-4 h-4 transition-transform group-hover:scale-110" />
                <span>{t('layout.header.trackStatus')}</span>
              </button>
            </div>

            {/* Mobile */}
            <div className="flex lg:hidden items-center gap-2 sm:gap-2.5 shrink-0">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                      "h-11 w-11 min-w-[44px] rounded-full bg-white border border-gray-200 shadow-sm",
                      "text-gray-600 hover:text-gray-900 hover:bg-gray-50 hover:border-gray-300",
                      "focus-visible:ring-2 focus-visible:ring-[#c0902c]/40 focus-visible:ring-offset-2"
                    )}
                    aria-label={t('layout.header.followUs')}
                  >
                    <AtSign className="h-5 w-5" strokeWidth={2} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  side="bottom"
                  sideOffset={6}
                  alignOffset={0}
                  className={cn(
                    "w-auto min-w-[180px] rounded-2xl border-2 border-gray-200 bg-white p-4",
                    "shadow-[0_10px_40px_rgba(0,0,0,0.12),0_0_0_1px_rgba(0,0,0,0.06)] outline-none ring-0"
                  )}
                >
                  <p className="text-xs font-medium text-gray-400 mb-3">
                    {t('layout.header.followUs')}
                  </p>
                  <div className="flex flex-wrap gap-3">
                    {environment.SOCIAL_FACEBOOK_URL && (
                      <SocialIcon href={environment.SOCIAL_FACEBOOK_URL} bgColor="bg-[#1877F2] hover:opacity-90" className="w-9 h-9">
                        <FacebookIcon />
                      </SocialIcon>
                    )}
                    {environment.SOCIAL_INSTAGRAM_URL && (
                      <SocialIcon href={environment.SOCIAL_INSTAGRAM_URL} bgColor="bg-gradient-to-br from-[#f09433] via-[#dc2743] to-[#bc1888] hover:opacity-90" className="w-9 h-9">
                        <InstagramIcon />
                      </SocialIcon>
                    )}
                    {environment.SOCIAL_TWITTER_URL && (
                      <SocialIcon href={environment.SOCIAL_TWITTER_URL} bgColor="bg-black hover:opacity-90" className="w-9 h-9">
                        <XIcon />
                      </SocialIcon>
                    )}
                    {environment.SOCIAL_LINKEDIN_URL && (
                      <SocialIcon href={environment.SOCIAL_LINKEDIN_URL} bgColor="bg-[#0A66C2] hover:opacity-90" className="w-9 h-9">
                        <LinkedInIcon />
                      </SocialIcon>
                    )}
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
              <LanguageSwitcher
                showLabel={false}
                showChevron={false}
                className={cn(
                  "h-12 w-12 min-w-[48px] rounded-full p-0 justify-center [&_span]:text-xl",
                  "bg-white border-2 border-gray-200 shadow-sm hover:bg-gray-50 hover:border-gray-300",
                  "focus-visible:ring-2 focus-visible:ring-[#c0902c]/40 focus-visible:ring-offset-2"
                )}
              />
              <button
                type="button"
                onClick={() => setTrackSheetOpen(true)}
                className={cn(
                  "flex items-center justify-center h-11 w-11 min-w-[44px] rounded-full bg-[#2e5784] hover:bg-[#234568] text-white",
                  "shadow-md shadow-blue-900/15 active:scale-[0.98] touch-manipulation"
                )}
                aria-label={t('layout.header.trackStatusTooltip')}
              >
                <FileSearch className="w-5 h-5" />
              </button>
              {(location.pathname === '/' || location.pathname === '/login') ? (
                <Link
                  to="/signup"
                  className={cn(
                    "flex items-center gap-1.5 sm:gap-2 bg-[#c0902c] hover:bg-[#b08020] text-white px-4 sm:px-5 py-2.5 rounded-full text-sm font-semibold",
                    "shadow-md shadow-amber-900/15 active:scale-[0.98] touch-manipulation min-h-[40px]"
                  )}
                >
                  <Lock className="w-4 h-4 shrink-0" />
                  <span>{t('navigation.signup')}</span>
                </Link>
              ) : (
                <Link
                  to="/login"
                  className={cn(
                    "flex items-center gap-1.5 sm:gap-2 bg-[#c0902c] hover:bg-[#b08020] text-white px-4 sm:px-5 py-2.5 rounded-full text-sm font-semibold",
                    "shadow-md shadow-amber-900/15 active:scale-[0.98] touch-manipulation min-h-[40px]"
                  )}
                >
                  <Lock className="w-4 h-4 shrink-0" />
                  <span>{t('navigation.login')}</span>
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
      <PublicTrackStatusSheet open={trackSheetOpen} onOpenChange={setTrackSheetOpen} />
    </header>
  );
};

export default PublicHeader;
